import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import ProductGallery from './ProductGallery';
import { ProductDetailsComponent as ProductDetails } from '~components/product';
import RelatedProducts from './RelatedProducts';
import ProductTitle from './ProductTitle';
import ProductBadge from './ProductBadge';
import { useViewport } from 'Common/hooks';

export default function ProductView({
    attributesToDisplay,
    className,
    currentMarketingBadges,
    Details = ProductDetails,
    detailsProps = {},
    Gallery = ProductGallery,
    galleryProps = {},
    isQuickView = false,
    isUserAuthenticated,
    itemStatusTooltip,
    productTitle,
    Related = RelatedProducts,
    relatedProductProps = {},
    showRelatedEntries = false,
    useChildTable,
}) {
    const viewport = useViewport();
    const isMobile = React.useMemo(
        () => viewport.is.lt(galleryProps.desktopBreakpoint ?? 'md'),
        [viewport, galleryProps.desktopBreakpoint]
    );

    return (
        <div className={cx('ProductView container-fluid', className)}>
            {!isMobile && !isQuickView ? (
                <>
                    <div className="ProductView__title">
                        <ProductBadge badges={currentMarketingBadges} />
                        <div className="divider"></div>
                        <ProductTitle productTitle={productTitle} useLargeProductTitle={true} />
                    </div>
                    <div className="ProductView__details">
                        <Gallery
                            className={cx('col-12', `col-${galleryProps.desktopBreakpoint ?? 'md'}-5`)}
                            {...galleryProps}
                        />
                        <Details
                            className={cx('col-12', `col-${detailsProps.desktopBreakpoint ?? 'md'}-7`, {
                                'col-lg-5': showRelatedEntries,
                            })}
                            attributesToDisplay={attributesToDisplay}
                            isUserAuthenticated={isUserAuthenticated}
                            itemStatusTooltip={itemStatusTooltip}
                            useChildTable={useChildTable}
                            {...detailsProps}
                        />
                        {showRelatedEntries ? (
                            <Related {...relatedProductProps} className="d-none d-lg-block col-2" />
                        ) : null}
                    </div>
                </>
            ) : (
                <div className={isQuickView && !isMobile ? 'd-flex' : ''}>
                    <Gallery
                        className={cx('col-12', `col-${galleryProps.desktopBreakpoint ?? 'md'}-5`)}
                        {...galleryProps}
                    />
                    <div className="w-100">
                        <div className="ProductView__title">
                            <ProductBadge badges={currentMarketingBadges} />
                            <div className="divider"></div>
                            <ProductTitle productTitle={productTitle} useLargeProductTitle={true} />
                        </div>
                        <div className="ProductView__details">
                            <Details
                                className={'w-100'}
                                attributesToDisplay={attributesToDisplay}
                                isQuickView={isQuickView}
                                isUserAuthenticated={isUserAuthenticated}
                                itemStatusTooltip={itemStatusTooltip}
                                useChildTable={useChildTable}
                                renderedInQuickView={isQuickView}
                                {...detailsProps}
                            />
                            {showRelatedEntries && !isQuickView ? (
                                <Related {...relatedProductProps} className="d-none d-lg-block col-2" />
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

ProductView.propTypes = {
    attributesToDisplay: px.arrayOf(px.object),
    className: px.string,
    currentMarketingBadges: px.arrayOf(px.string),
    Details: px.elementType,
    detailsProps: px.objectOf(px.any),
    Gallery: px.elementType,
    galleryProps: px.objectOf(px.any),
    isQuickView: px.bool,
    isUserAuthenticated: px.oneOfType([px.string, px.bool]),
    itemStatusTooltip: px.oneOfType([px.string, px.bool]),
    productTitle: px.string,
    Related: px.elementType,
    relatedProductProps: px.object,
    showRelatedEntries: px.bool,
    useChildTable: px.bool,
};
